import { useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useToast } from "@/ds";
import { useMediaQuery } from "@/hooks";
import { alterarSituacaoCurva } from "@/modules/financeiro-configuracoes";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { useChaveIdempotencia } from "@/shared/hooks";
import type { CurvaItem } from "@/modules/financeiro-configuracoes";
import { FINANCEIRO_MOBILE_QUERY } from "../financeiro-configuracoes.constants";
import {
  CATEGORIA_TODAS,
  CURVAS_ATIVAR_LABEL,
  CURVA_ATIVADA,
  CURVA_CRIADA,
  CURVA_EDITADA,
  CURVA_INATIVADA,
  CURVAS_INATIVAR_LABEL,
  CURVAS_SITUACAO_ERRO,
  CURVAS_ULTIMA_ATIVA,
  INATIVAR_CONFIRMAR,
  INATIVAR_EFEITOS,
  INATIVAR_EFEITOS_TITULO,
  INATIVAR_INTRO,
  INATIVAR_SUBTITULO,
  INATIVAR_TITULO,
} from "./curvas.constants";
import type { CategoriaFiltro } from "./curvas.constants";
import {
  formatAlteradaEm,
  formatFase,
  formatMeses,
  formatPercentual,
  formatResumo,
  montarPrevia,
  rotuloCategoria,
} from "./curvas.utils";
import { useCurvasDados } from "./hooks/use-curvas-dados";
import type {
  AcaoPendente,
  CurvaLinha,
  CurvasConteudo,
  CurvasViewModel,
  SituacaoDialogo,
} from "./types-curvas";

const emUso = (curva: CurvaItem) => curva.uso.escopos + curva.uso.obras > 0;

function montarMesesExtras(curva: CurvaItem): string | null {
  const total = curva.antes.meses + curva.depois.meses;
  return total > 0 ? `+ ${formatMeses(total)}` : null;
}

export function useCurvas(): CurvasViewModel {
  const { hasPermissao } = usePermissoes();
  const podeEditar = hasPermissao(HUB_PERMISSOES.FINANCEIRO_EDIT_CONFIGURACAO);
  const [categoria, setCategoria] = useState<CategoriaFiltro>(CATEGORIA_TODAS);
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [expandidas, setExpandidas] = useState<string[]>([]);
  const [acaoPendente, setAcaoPendente] = useState<AcaoPendente | null>(null);
  const [aplicandoCurvaId, setAplicandoCurvaId] = useState<string | null>(null);
  const [inativando, setInativando] = useState<CurvaItem | null>(null);
  const [ativandoId, setAtivandoId] = useState<string | null>(null);
  const [erroSituacao, setErroSituacao] = useState<string | null>(null);
  const { comChave } = useChaveIdempotencia();
  const toast = useToast();
  const mobile = useMediaQuery(FINANCEIRO_MOBILE_QUERY);

  const dados = useCurvasDados({
    categoria: categoria === CATEGORIA_TODAS ? undefined : categoria,
    incluirInativas: mostrarInativas,
  });

  const alterarSituacao = (curva: CurvaItem, situacao: "ATIVA" | "INATIVA") =>
    comChave({ id: curva.id, situacao }, (idempotencyKey) =>
      alterarSituacaoCurva(curva.id, situacao, { idempotencyKey }),
    );

  const ativar = async (curva: CurvaItem) => {
    setErroSituacao(null);
    setAtivandoId(curva.id);
    try {
      await alterarSituacao(curva, "ATIVA");
      toast.success(CURVA_ATIVADA);
      dados.recarregar();
    } catch (falha) {
      setErroSituacao(getErrorMessage(falha) || CURVAS_SITUACAO_ERRO);
    } finally {
      setAtivandoId(null);
    }
  };

  const ativasDaCategoria = (categoriaDaCurva: CurvaItem["categoria"]) =>
    dados.curvas.filter((curva) => curva.ativo && curva.categoria === categoriaDaCurva).length;

  const linhas: CurvaLinha[] = dados.curvas.map((curva) => ({
    id: curva.id,
    testId: `curva-linha-${curva.id}`,
    toggleTestId: `curva-toggle-${curva.id}`,
    previaId: `curva-previa-${curva.id}`,
    editarTestId: `curva-editar-${curva.id}`,
    nome: curva.nome,
    categoria: rotuloCategoria(curva.categoria),
    antes: formatFase(curva.antes.percentual, curva.antes.meses),
    durante: formatPercentual(curva.durante.percentual),
    apos: formatFase(curva.depois.percentual, curva.depois.meses),
    mesesExtras: montarMesesExtras(curva),
    alteradaEm: formatAlteradaEm(curva.alteradaEm),
    alteradaPor: curva.alteradaPor,
    inativa: !curva.ativo,
    expandida: expandidas.includes(curva.id),
    previa: montarPrevia(curva),
    alternar: () =>
      setExpandidas((atuais) =>
        atuais.includes(curva.id) ? atuais.filter((id) => id !== curva.id) : [...atuais, curva.id],
      ),
    editar: podeEditar ? () => setAcaoPendente({ tipo: "editar", curva }) : undefined,
    situacao: podeEditar
      ? {
          rotulo: `${curva.ativo ? CURVAS_INATIVAR_LABEL : CURVAS_ATIVAR_LABEL} ${curva.nome}`,
          testId: `curva-situacao-${curva.id}`,
          desabilitado:
            ativandoId !== null || (curva.ativo && ativasDaCategoria(curva.categoria) <= 1),
          tooltip:
            curva.ativo && ativasDaCategoria(curva.categoria) <= 1 ? CURVAS_ULTIMA_ATIVA : null,
          executar: curva.ativo
            ? () => {
                setErroSituacao(null);
                setInativando(curva);
              }
            : () => void ativar(curva),
        }
      : undefined,
  }));

  const situacaoDialogo: SituacaoDialogo | null = inativando
    ? {
        dialogo: {
          acao: "inativar",
          titulo: INATIVAR_TITULO,
          subtitulo: INATIVAR_SUBTITULO,
          oQueAcontece: INATIVAR_EFEITOS,
          confirmLabel: INATIVAR_CONFIRMAR,
          tituloEfeitos: INATIVAR_EFEITOS_TITULO,
          onConfirmar: async () => {
            await alterarSituacao(inativando, "INATIVA");
            toast.success(CURVA_INATIVADA);
            setInativando(null);
            dados.recarregar();
          },
          onCancelar: () => setInativando(null),
        },
        intro: INATIVAR_INTRO,
        nome: inativando.nome,
        categoria: rotuloCategoria(inativando.categoria),
        resumo: formatResumo(inativando),
      }
    : null;

  let conteudo: CurvasConteudo = "lista";
  if (dados.semPermissao) conteudo = "sem-permissao";
  else if (dados.erro) conteudo = "erro";
  else if (dados.carregando && linhas.length === 0) conteudo = "carregando";
  else if (linhas.length === 0) conteudo = "vazio";

  return {
    conteudo,
    erro: dados.erro,
    categoria,
    alterarCategoria: setCategoria,
    mostrarInativas,
    alternarInativas: () => setMostrarInativas((atual) => !atual),
    linhas,
    recarregar: dados.recarregar,
    nova: podeEditar ? () => setAcaoPendente({ tipo: "nova" }) : undefined,
    acaoPendente,
    fecharAcao: () => setAcaoPendente(null),
    concluirAcao: () => {
      if (acaoPendente) {
        toast.success(acaoPendente.tipo === "nova" ? CURVA_CRIADA : CURVA_EDITADA);
      }
      if (acaoPendente?.tipo === "editar" && emUso(acaoPendente.curva)) {
        setAplicandoCurvaId(acaoPendente.curva.id);
      }
      setAcaoPendente(null);
      dados.recarregar();
    },
    situacaoDialogo,
    aplicacaoDialogo: aplicandoCurvaId
      ? {
          tipo: "CURVA",
          referenciaId: aplicandoCurvaId,
          onClose: () => setAplicandoCurvaId(null),
        }
      : null,
    erroSituacao,
    mobile,
  };
}
